/**
 * Terminal tab bar component.
 * Displays tabs with connection status, add/close buttons, and split controls.
 *
 * @module components/terminal/TerminalTabBar
 */

import {
  Terminal as TerminalIcon,
  X,
  Plus,
  Loader2,
  AlertCircle,
  CheckCircle,
  Maximize2,
  Minimize2,
  PanelLeftClose,
  PanelLeft,
  Copy,
  Check,
} from 'lucide-react';
import type { TerminalTabBarProps } from './types';
import { MAX_TERMINAL_TABS } from './types';

/** Connection status indicator for a tab */
function ConnectionIndicator({ state }: { state: string }) {
  switch (state) {
    case 'connecting':
      return <Loader2 className="h-2.5 w-2.5 animate-spin text-yellow-400" />;
    case 'connected':
      return <CheckCircle className="h-2.5 w-2.5 text-green-400" />;
    case 'error':
      return <AlertCircle className="h-2.5 w-2.5 text-red-400" />;
    default:
      return <div className="h-2 w-2 rounded-full bg-gray-500" />;
  }
}

/**
 * Tab bar with add/close/split controls
 */
export function TerminalTabBar({
  tabs,
  activeTabId,
  isFullscreen,
  activeTabHasSplit,
  onAddTab,
  onCloseTab,
  onSelectTab,
  onToggleSplit,
  onToggleFullscreen,
  onClose,
  onCopy,
  copyState = 'idle',
}: TerminalTabBarProps) {
  const canAddTab = tabs.length < MAX_TERMINAL_TABS;

  return (
    <div className="flex items-center justify-between bg-[#161616] border-b border-[#2a2a2a]">
      {/* Tab List */}
      <div className="flex items-center flex-1 overflow-x-auto">
        <div className="flex items-center">
          {tabs.map((tab) => {
            const isActive = tab.id === activeTabId;

            return (
              <div
                key={tab.id}
                onClick={() => onSelectTab(tab.id)}
                className={`
                  group flex items-center gap-2 px-3 py-2 cursor-pointer
                  border-r border-[#2a2a2a] min-w-[120px] max-w-[180px]
                  transition-colors
                  ${
                    isActive
                      ? 'bg-[#0a0a0a] text-white'
                      : 'bg-[#161616] text-gray-400 hover:bg-[#1a1a1a] hover:text-gray-200'
                  }
                `}
              >
                {/* Connection indicator */}
                <ConnectionIndicator state={tab.connectionState} />

                {/* Tab name */}
                <span className="text-sm truncate flex-1">{tab.name}</span>

                {/* Split indicator - show if this tab has a split */}
                {tab.split && (
                  <span className="text-xs text-blue-400" title="Has split view">
                    ⫿
                  </span>
                )}

                {/* Close button (don't show if it's the only tab) */}
                {tabs.length > 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onCloseTab(tab.id);
                    }}
                    className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#333] transition-opacity"
                    title="Close terminal"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            );
          })}

          {/* Add Tab Button */}
          <button
            onClick={onAddTab}
            disabled={!canAddTab}
            className={`
              flex items-center justify-center px-3 py-2
              transition-colors
              ${
                canAddTab
                  ? 'text-gray-400 hover:text-gray-200 hover:bg-[#1a1a1a]'
                  : 'text-gray-600 cursor-not-allowed'
              }
            `}
            title={canAddTab ? 'New terminal' : `Maximum ${MAX_TERMINAL_TABS} tabs reached`}
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Right-side controls */}
      <div className="flex items-center gap-1 px-2 border-l border-[#2a2a2a]">
        {/* Copy button */}
        {onCopy && (
          <button
            onClick={onCopy}
            className={`
              p-1.5 rounded transition-colors
              ${
                copyState === 'copied'
                  ? 'text-green-400 bg-green-400/20'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-[#2a2a2a]'
              }
            `}
            title={copyState === 'copied' ? 'Copied!' : 'Copy terminal content'}
          >
            {copyState === 'copied' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </button>
        )}

        {/* Split button - toggles split for active tab */}
        <button
          onClick={onToggleSplit}
          className={`
            p-1.5 rounded transition-colors
            ${
              activeTabHasSplit
                ? 'text-blue-400 bg-blue-400/20 hover:bg-blue-400/30'
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
            }
          `}
          title={activeTabHasSplit ? 'Close split view' : 'Split terminal'}
        >
          {activeTabHasSplit ? (
            <PanelLeftClose className="h-4 w-4" />
          ) : (
            <PanelLeft className="h-4 w-4" />
          )}
        </button>

        {/* Fullscreen button */}
        <button
          onClick={onToggleFullscreen}
          className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-[#2a2a2a] rounded transition-colors"
          title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        >
          {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </button>

        {/* Terminal icon + Close button */}
        <div className="flex items-center gap-2 ml-2 pl-2 border-l border-[#2a2a2a]">
          <TerminalIcon className="h-4 w-4 text-gray-400" />
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-[#2a2a2a] rounded transition-colors"
              title="Close terminal"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
